import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";
import { ManuscriptProvider } from "@/contexts/ManuscriptContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PlatformProvider } from "@/contexts/PlatformContext";
import { PwaUpdateProvider } from "@/contexts/PwaUpdateContext";
import { ReloadPrompt } from "@/components/ReloadPrompt";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CanonicalLink } from "@/components/CanonicalLink";
import { checkAndMigrateWebLegacyData } from "@/lib/migration/webLegacyMigrator";
import { handleAuthDeepLink } from "@/lib/deepLink";
import { startSupportSyncService } from "@/lib/supportApi";

// Lazy load all page components for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Library = lazy(() => import("./pages/Library"));
const CreateManuscriptWizard = lazy(() => import("./pages/CreateManuscriptWizard"));
const Editor = lazy(() => import("./pages/Editor"));
const Settings = lazy(() => import("./pages/Settings"));
const Auth = lazy(() => import("./pages/Auth"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Outline = lazy(() => import("./pages/Outline"));
const CodexEntry = lazy(() => import("./pages/CodexEntry"));
const Xray = lazy(() => import("./pages/Xray"));
const DbDiagnostic = lazy(() => import("./pages/DbDiagnostic"));
const BetaSignup = lazy(() => import("./pages/BetaSignup"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback - instant cut, no animation (e-ink friendly)
const PageLoader = () => (
  <div className="h-screen overflow-hidden flex items-center justify-center bg-background">
    <span className="text-muted-foreground">Loading...</span>
  </div>
);

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAndMigrateWebLegacyData();
    const cleanupSupportSync = startSupportSyncService();
    return () => {
      cleanupSupportSync();
    };
  }, []);

  useEffect(() => {
    const backListener = CapApp.addListener("backButton", ({ canGoBack }) => {
      if (location.pathname === "/" || location.pathname === "/library") {
        CapApp.exitApp();
      } else if (canGoBack) {
        navigate(-1);
      } else {
        navigate("/library");
      }
    });

    return () => {
      backListener.then((h) => h.remove()).catch(() => {});
    };
  }, [navigate, location]);

  // Flush pending writes when app is backgrounded (Android + Web PWA)
  useEffect(() => {
    const stateListener = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        // Flush any pending debounced cloud writes
        import('@/lib/repository/webRepository').then(({ webRepository }) => {
          webRepository.flushPendingDebounces();
        }).catch(() => {});
      }
    });

    return () => {
      stateListener.then((h) => h.remove()).catch(() => {});
    };
  }, []);

  // Handle native deep link callbacks (e.g. Supabase OAuth Google Sign-In)
  useEffect(() => {
    // Check if app was cold-launched via a deep link URL
    CapApp.getLaunchUrl().then((launchUrl) => {
      if (launchUrl?.url) {
        handleAuthDeepLink(launchUrl.url, () => navigate('/library'));
      }
    }).catch(() => {});

    // Listen for warm-launch deep links when app is resumed
    const urlListener = CapApp.addListener("appUrlOpen", ({ url }) => {
      handleAuthDeepLink(url, () => navigate('/library'));
    });

    return () => {
      urlListener.then((h) => h.remove()).catch(() => {});
    };
  }, [navigate]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public marketing routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/delete-data" element={<Navigate to="/data-deletion" replace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/closed-beta" element={<BetaSignup />} />
        <Route path="/closedbeta" element={<BetaSignup />} />
        <Route path="/closed-beta-signup" element={<BetaSignup />} />
        <Route path="/beta" element={<BetaSignup />} />
        <Route path="/join-beta" element={<BetaSignup />} />
        
        {/* Protected app routes */}
        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
        <Route path="/library/new" element={<ProtectedRoute><CreateManuscriptWizard /></ProtectedRoute>} />
        <Route path="/editor/:manuscriptId" element={<ProtectedRoute><Editor /></ProtectedRoute>} />
        <Route path="/outline/:manuscriptId" element={<ProtectedRoute><Outline /></ProtectedRoute>} />
        <Route path="/codex/:manuscriptId/:entryId" element={<ProtectedRoute><CodexEntry /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/guide" element={<Navigate to="/faq" replace />} />
        <Route path="/xray/:manuscriptId" element={<ProtectedRoute><Xray /></ProtectedRoute>} />
        <Route path="/db" element={<DbDiagnostic />} />
        <Route path="/db-diagnostic" element={<DbDiagnostic />} />
        
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PlatformProvider>
          <ManuscriptProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <PwaUpdateProvider>
                  <CanonicalLink />
                  <AppContent />
                  <ReloadPrompt />
                </PwaUpdateProvider>
              </BrowserRouter>
            </TooltipProvider>
          </ManuscriptProvider>
        </PlatformProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
